import React, { Component } from 'react'
import { StyleSheet, css } from 'aphrodite'
import moment from 'moment-timezone'
import { Helper } from '../helpers/Helper'

function sanitizeArray(value) {
  return Array.isArray(value) ? value : []
}

function safeStr(value) {
  if (value == null) return ''
  return String(value)
}

export class WhatsappTemplatePreview extends Component {
  constructor(props) {
    super(props)
    this.state = { now: new Date(), isBodyExpanded: false, loadedHeaderImageUrl: '' }
    this.timeIntervalId = null
  }

  componentDidMount() {
    if (this.props.messageBubbleMode) return
    this.timeIntervalId = setInterval(() => {
      this.setState({ now: new Date() })
    }, 60000)
  }

  componentWillUnmount() {
    if (this.timeIntervalId != null) {
      clearInterval(this.timeIntervalId)
      this.timeIntervalId = null
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.components !== this.props.components) {
      const prevHeaderComp = sanitizeArray(prevProps.components).find(c => (c && c.type) === 'HEADER')
      const nextHeaderComp = this.getHeaderComponent()
      const prevHeaderFormat = String((prevHeaderComp && prevHeaderComp.format) || '').toUpperCase()
      const nextHeaderFormat = String((nextHeaderComp && nextHeaderComp.format) || '').toUpperCase()
      const prevHeaderMediaUrl = this.getHeaderMediaUrl(prevHeaderComp)
      const nextHeaderMediaUrl = this.getHeaderMediaUrl(nextHeaderComp)
      const didHeaderMediaChange = (
        prevHeaderFormat !== nextHeaderFormat ||
        prevHeaderMediaUrl !== nextHeaderMediaUrl
      )
      this.setState({
        isBodyExpanded: false,
        loadedHeaderImageUrl: didHeaderMediaChange ? '' : this.state.loadedHeaderImageUrl,
      })
    }
  }

  formatBubbleTime = (date) => {
    if (!date || !(date instanceof Date)) return ''
    const timezone = Helper.getLocalTimezone()
    return moment(date).tz(timezone).format('h:mm a')
  }

  renderBubbleTimeLabel = () => {
    const label = safeStr(this.props.bubbleTimeLabel).trim()
    if (label) return label
    return this.formatBubbleTime(this.state.now)
  }

  getComponents = () => sanitizeArray(this.props.components)

  getComponentByType = (type) => this.getComponents().find(c => (c && c.type) === type)

  getHeaderComponent = () => this.getComponentByType('HEADER')

  getBodyComponent = () => this.getComponentByType('BODY')

  getFooterComponent = () => this.getComponentByType('FOOTER')

  getButtonsComponent = () => this.getComponentByType('BUTTONS')

  getHeaderExampleValue = (headerComp, varIndex) => {
    const ex = (headerComp && headerComp.example) || {}
    const list = sanitizeArray(ex.header_text)
    const value = list[varIndex - 1]
    if (value == null || String(value).trim() === '') return `{{${varIndex}}}`
    return String(value)
  }

  getBodyExampleValue = (bodyComp, varIndex) => {
    const ex = (bodyComp && bodyComp.example) || {}
    const rows = sanitizeArray(ex.body_text)
    const values = sanitizeArray(rows[0])
    const value = values[varIndex - 1]
    if (value == null || String(value).trim() === '') return `{{${varIndex}}}`
    return String(value)
  }

  replaceTemplateVariables = (text, valueResolver) => {
    if (text == null) return ''
    return String(text).replace(/\{\{([^{}]*\d+[^{}]*)\}\}/g, (match, inner) => {
      const nMatch = String(inner).match(/(\d+)/)
      if (!nMatch) return match
      const idx = parseInt(nMatch[1], 10)
      const resolved = valueResolver(idx)
      if (resolved == null) return match
      return String(inner).replace(/(\d+)/, String(resolved))
    })
  }

  formatWhatsappTextNodes = (rawText) => {
    const lines = String(rawText || '').split('\n')
    return lines.map((line, i) => (
      <span key={`line_${i}`} className={css(styles.lineWrap)}>
        {this.renderLineWithListSupport(line)}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    ))
  }

  renderLineWithListSupport = (line) => {
    const raw = String(line || '')
    const trimmed = raw.trimStart()
    if (trimmed.startsWith('- ')) {
      const bulletText = trimmed.slice(2)
      return (
        <span className={css(styles.bulletLine)}>
          <span className={css(styles.bulletGlyph)}>•</span>
          <span className={css(styles.bulletText)}>{this.renderInlineFormatting(bulletText)}</span>
        </span>
      )
    }
    return this.renderInlineFormatting(raw)
  }

  renderInlineFormatting = (text) => {
    const nodes = []
    const input = String(text || '')
    const tokenRegex = /(\*_[^]+?_\*|_\*[^]+?\*_|\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```)/g
    let lastIndex = 0
    let match
    let key = 0

    while ((match = tokenRegex.exec(input)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(<span key={`txt_${key++}`}>{input.slice(lastIndex, match.index)}</span>)
      }
      const token = match[0]
      if (token.startsWith('*_') && token.endsWith('_*')) {
        const content = token.slice(2, -2)
        nodes.push(<strong key={`fmt_${key++}`}><em>{content}</em></strong>)
      } else if (token.startsWith('_*') && token.endsWith('*_')) {
        const content = token.slice(2, -2)
        nodes.push(<em key={`fmt_${key++}`}><strong>{content}</strong></em>)
      } else if (token.startsWith('*') && token.endsWith('*')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<strong key={`fmt_${key++}`}>{content}</strong>)
      } else if (token.startsWith('_') && token.endsWith('_')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<em key={`fmt_${key++}`}>{content}</em>)
      } else if (token.startsWith('~') && token.endsWith('~')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<span key={`fmt_${key++}`} className={css(styles.strike)}>{content}</span>)
      } else if (token.startsWith('```') && token.endsWith('```')) {
        nodes.push(<code key={`fmt_${key++}`} className={css(styles.code)}>{token.slice(3, -3)}</code>)
      } else {
        nodes.push(<span key={`fmt_${key++}`}>{token}</span>)
      }
      lastIndex = tokenRegex.lastIndex
    }

    if (lastIndex < input.length) {
      nodes.push(<span key={`txt_${key++}`}>{input.slice(lastIndex)}</span>)
    }
    return nodes
  }

  renderHeader = () => {
    const headerComp = this.getHeaderComponent()
    if (!headerComp) return null
    const bubbleMode = !!this.props.messageBubbleMode
    const format = String(headerComp.format || '').toUpperCase()

    if (format === 'TEXT') {
      const rendered = this.replaceTemplateVariables(headerComp.text || '', idx =>
        this.getHeaderExampleValue(headerComp, idx)
      )
      return (
        <div className={css(styles.headerText, bubbleMode && styles.headerTextBubbleMode)}>
          {this.formatWhatsappTextNodes(rendered)}
        </div>
      )
    }

    const mediaUrl = this.getHeaderMediaUrl(headerComp)
    if (!mediaUrl) {
      return (
        <div className={css(styles.missingMedia)}>
          {format || 'MEDIA'}
        </div>
      )
    }

    if (format === 'IMAGE') {
      const imageLoaded = this.state.loadedHeaderImageUrl === mediaUrl
      return (
        <div className={css(
          styles.headerImageWrap,
          bubbleMode && styles.headerImageWrapBubbleMode,
          !imageLoaded && styles.headerImageWrapLoading,
          bubbleMode && !imageLoaded && styles.headerImageWrapLoadingBubbleMode
        )}>
          {!imageLoaded && <div className={css(styles.headerImagePlaceholder)} />}
          <img
            src={mediaUrl}
            alt='whatsapp-header'
            className={css(
              styles.headerImage,
              bubbleMode && styles.headerImageBubbleMode,
              imageLoaded ? styles.headerImageVisible : styles.headerImageHidden
            )}
            onLoad={() => {
              if (this.state.loadedHeaderImageUrl !== mediaUrl) {
                this.setState({ loadedHeaderImageUrl: mediaUrl })
              }
            }}
          />
        </div>
      )
    }
    if (format === 'VIDEO') {
      const video = (
        <video
          className={css(styles.headerVideo, bubbleMode && styles.headerVideoBubbleMode)}
          controls
          controlsList='nodownload noplaybackrate'
          src={mediaUrl}
        />
      )
      if (bubbleMode) {
        return <div className={css(styles.headerVideoWrapBubbleMode)}>{video}</div>
      }
      return video
    }
    if (format === 'DOCUMENT') {
      const doc = this.buildDocumentMetaFromUrl(mediaUrl, String(headerComp.fileName || '').trim())
      const documentSet = headerComp.documentSet
      const documentContent =  (
        <div className={css(styles.documentTop)}>
          <div className={css(styles.documentLeft)}>
            <div className={css(styles.documentFileTile)}>
              <span className={`material-icons-outlined ${css(styles.documentFileIcon)}`}>description</span>
              <div className={css(styles.documentFileType)}>{doc.extLabel}</div>
            </div>
          </div>
          <div className={css(styles.documentMeta)}>
            <div className={css(styles.documentName)} title={doc.fileName}>
              {doc.fileName}
            </div>
            <div className={css(styles.documentSub)}>Document</div>
          </div>
        </div>
      )
      if (documentSet) {
        return (
          <a
            href={mediaUrl}
            target='_blank'
            rel='noopener noreferrer'
            className={css(styles.documentCard, bubbleMode && styles.documentCardBubbleMode)}
          >
            {documentContent}
          </a>
        )
      }
      return (
        <div className={css(styles.documentCard, bubbleMode && styles.documentCardBubbleMode)}>
          {documentContent}
        </div>
      )
    }

    return (
      <div className={css(styles.missingMedia)}>
        {format}
      </div>
    )
  }

  getHeaderMediaUrl = (headerComp) => {
    const ex = (headerComp && headerComp.example) || {}
    const handles = sanitizeArray(ex.header_handle)
    if (handles[0]) return String(handles[0])
    return ''
  }

  buildDocumentMetaFromUrl = (url, configuredName = '') => {
    const raw = String(url || '')
    const safe = raw.split('#')[0].split('?')[0]
    const last = safe.split('/').filter(Boolean).pop() || ''
    const inferredName = last ? decodeURIComponent(last) : ''
    const fileNameRaw = configuredName || inferredName
    const fileName = (fileNameRaw && fileNameRaw.length <= 60) ? fileNameRaw : (fileNameRaw ? `${fileNameRaw.slice(0, 57)}...` : 'Document.pdf')
    const ext = (last && last.includes('.')) ? last.split('.').pop().toLowerCase() : ''
    const extLabel = (ext && ext.length <= 5) ? ext.toUpperCase() : 'PDF'
    return { fileName, ext, extLabel }
  }

  isBodyLong = (text) => {
    const s = String(text || '')
    const lines = s.split('\n').length
    return s.length > 220 || lines > 4
  }

  getCollapsedBodyText = (text) => {
    const s = String(text || '').trim()
    const lines = s.split('\n')
    if (lines.length > 4) {
      return `${lines.slice(0, 4).join('\n').trimEnd()}...`
    }
    if (s.length <= 180) return s
    return `${s.slice(0, 177).trimEnd()}...`
  }

  renderBody = () => {
    const bodyComp = this.getBodyComponent()
    if (!bodyComp) return null
    const rendered = this.replaceTemplateVariables(bodyComp.text || '', idx =>
      this.getBodyExampleValue(bodyComp, idx)
    )
    const bubbleMode = !!this.props.messageBubbleMode
    const bodyIsLong = this.isBodyLong(rendered)
    const collapsedBodyText = this.getCollapsedBodyText(rendered)
    const hasHiddenBodyText = collapsedBodyText !== String(rendered || '').trim()
    const showReadMore = !bubbleMode && bodyIsLong && hasHiddenBodyText && !this.state.isBodyExpanded
    const bodyText = (showReadMore || this.state.isBodyExpanded) ? (this.state.isBodyExpanded ? rendered : collapsedBodyText) : rendered
    return (
      <div className={css(styles.bodyText, bubbleMode && styles.bodyTextBubbleMode)}>
        {this.formatWhatsappTextNodes(bodyText)}
        {showReadMore && (
          <button
            type='button'
            className={css(styles.readMoreButton)}
            onClick={() => this.setState({ isBodyExpanded: true })}
          >
            Read more
          </button>
        )}
      </div>
    )
  }

  renderFooter = () => {
    const footerComp = this.getFooterComponent()
    if (!footerComp || !footerComp.text) return null
    return <span className={css(styles.footerText)}>{footerComp.text}</span>
  }

  renderButtonText = (button, buttonIndex) => {
    if (!button) return ''
    const fallback = button.text || ''
    return fallback || `Button ${buttonIndex + 1}`
  }

  renderButtonRow = (button, index) => {
    if (!button) return null
    const bubbleMode = !!this.props.messageBubbleMode
    const type = String(button.type || '').toUpperCase()
    const text = this.renderButtonText(button, index)
    const label = text || (type === 'PHONE_NUMBER' ? button.phone_number : button.url) || `Button ${index + 1}`
    const iconName = this.getButtonIconName(type)
    const href = this.getButtonHref(button, type)
    const disableInteractiveInBubble = bubbleMode && (type === 'FLOW' || type === 'LIST')
    const hasAction = !disableInteractiveInBubble && (Boolean(href) || type === 'QUICK_REPLY' || type === 'FLOW')

    return (
      <button
        key={`btn_${index}`}
        type='button'
        className={css(styles.buttonRow)}
        onClick={() => this.handleButtonClick(href, type)}
        disabled={!hasAction}
      >
        {iconName && (
          <span className={css(styles.buttonIconWrap)} aria-hidden='true'>
            <span className={`material-icons-outlined ${css(styles.buttonIcon)}`}>
              {iconName}
            </span>
          </span>
        )}
        <span>{label}</span>
      </button>
    )
  }

  getButtonHref = (button, type) => {
    if (!button) return ''
    if (type === 'URL') {
      const raw = this.renderUrlButtonHref(button)
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      return `https://${raw}`
    }
    if (type === 'PHONE' || type === 'PHONE_NUMBER') {
      const phone = (button.phone_number || '').trim()
      if (!phone) return ''
      return `tel:${phone}`
    }
    return ''
  }

  renderUrlButtonHref = (button) => {
    if (!button) return ''
    const urlTemplate = String(button.url || '').trim()
    if (!urlTemplate) return ''
    const hasVars = /\{\{\d+\}\}/.test(urlTemplate)
    const ex = sanitizeArray(button.example)
    if (ex.length > 0) {
      const first = String(ex[0] || '').trim()
      if (ex.length === 1 && /^https?:\/\//i.test(first)) return first
    }
    if (!hasVars) return urlTemplate
    const replaced = urlTemplate.replace(/\{\{(\d+)\}\}/g, (m, n) => {
      const idx = parseInt(n, 10)
      const v = ex[idx - 1]
      if (v == null || String(v).trim() === '') return m
      return String(v)
    })
    return replaced
  }

  handleButtonClick = (href, type) => {
    if (this.props.messageBubbleMode && (type === 'FLOW' || type === 'LIST')) return
    if (!href) return
    if (href.startsWith('tel:')) {
      window.location.href = href
      return
    }
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  getButtonIconName = (type) => {
    if (type === 'PHONE' || type === 'PHONE_NUMBER') return 'phone'
    if (type === 'URL') return 'open_in_new'
    if (type === 'QUICK_REPLY') return 'reply'
    if (type === 'FLOW') return 'assignment'
    return ''
  }

  renderButtons = () => {
    const buttonComp = this.getButtonsComponent()
    const buttons = sanitizeArray(buttonComp && buttonComp.buttons)
    if (buttons.length === 0) return null
    return <div className={css(styles.buttonsWrap)}>{buttons.map(this.renderButtonRow)}</div>
  }

  renderMessageInner = () => {
    const bubbleMode = !!this.props.messageBubbleMode
    return (
      <div className={css(bubbleMode ? styles.bubbleModeBubble : styles.bubble)}>
        {this.renderHeader()}
        {this.renderBody()}
        <div className={css(styles.metaRow, bubbleMode && styles.metaRowBubbleMode)}>
          {this.renderFooter()}
          <div className={css(styles.metaRowTrailing, bubbleMode && styles.metaRowTrailingBubbleMode)}>
            <span className={css(styles.timeText, bubbleMode && styles.timeTextBubbleMode)}>
              {this.renderBubbleTimeLabel()}
            </span>
            {bubbleMode && this.props.statusSlot ? (
              <span className={css(styles.metaRowStatusSlot)}>{this.props.statusSlot}</span>
            ) : null}
          </div>
        </div>
        {this.renderButtons()}
      </div>
    )
  }

  render() {
    if (this.props.messageBubbleMode) {
      return (
        <div className={css(styles.bubbleModeRoot)}>
          {this.renderMessageInner()}
        </div>
      )
    }
    return (
      <div className={css(styles.phoneShell)}>
        <div className={css(styles.chatArea)}>
          {this.props.hideMessageBubble ? null : this.renderMessageInner()}
        </div>
      </div>
    )
  }
}

const styles = StyleSheet.create({
  bubbleModeRoot: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
  },
  bubbleModeBubble: {
    marginLeft: 0,
    maxWidth: '100%',
    minWidth: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    boxShadow: 'none',
    color: '#111b21',
    overflow: 'hidden',
  },
  metaRowBubbleMode: {
    padding: '6px 0 2px',
  },
  metaRowTrailing: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    flex: '0 0 auto',
    marginLeft: 'auto',
  },
  metaRowTrailingBubbleMode: {
    gap: 5,
  },
  metaRowStatusSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  timeTextBubbleMode: {
    marginLeft: 0,
  },
  headerTextBubbleMode: {
    padding: 0,
  },
  bodyTextBubbleMode: {
    padding: 0,
  },
  documentCardBubbleMode: {
    margin: 0,
  },
  headerImageWrapBubbleMode: {
    width: 220,
    maxWidth: '100%',
    maxHeight: 'none',
    aspectRatio: '16 / 9',
    borderRadius: 6,
  },
  headerImageWrapLoadingBubbleMode: {
    minHeight: 0,
  },
  headerImageBubbleMode: {
    width: '100%',
    height: 'auto',
    maxHeight: '50vh',
    objectFit: 'contain',
  },
  headerVideoWrapBubbleMode: {
    width: 220,
    maxWidth: '100%',
  },
  headerVideoBubbleMode: {
    width: '100%',
    maxHeight: '50vh',
    display: 'block',
  },
  phoneShell: {
    width: 340,
    maxWidth: '100%',
    margin: '0 auto',
    borderRadius: 24,
    backgroundColor: '#e5ddd5',
    border: '1px solid #d1d7db',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
  chatArea: {
    padding: 12,
    minHeight: 320,
    backgroundImage: 'radial-gradient(#ffffff66 1px, transparent 1px)',
    backgroundSize: '8px 8px',
  },
  bubble: {
    marginLeft: 'auto',
    maxWidth: '92%',
    minWidth: '72%',
    backgroundColor: '#e7ffdb',
    borderRadius: 8,
    boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
    color: '#111b21',
    overflow: 'hidden',
  },
  headerText: {
    padding: '12px 12px 0',
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '23px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  headerImage: {
    width: '100%',
    display: 'block',
    maxHeight: 240,
    objectFit: 'contain',
  },
  headerImageWrap: {
    width: '100%',
    maxHeight: 240,
    position: 'relative',
    overflow: 'visible',
    backgroundColor: '#dfe4e8',
  },
  headerImageWrapLoading: {
    minHeight: 184,
  },
  headerImagePlaceholder: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'linear-gradient(100deg, #d7dde2 30%, #e8edf1 50%, #d7dde2 70%)',
    backgroundSize: '200% 100%',
    animationName: {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
    animationDuration: '1.2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  headerImageHidden: {
    opacity: 0,
    transform: 'scale(1.01)',
  },
  headerImageVisible: {
    opacity: 1,
    transform: 'scale(1)',
    transition: 'opacity 220ms ease, transform 220ms ease',
  },
  headerVideo: {
    width: '100%',
    display: 'block',
    maxHeight: 240,
    backgroundColor: '#000',
  },
  documentCard: {
    margin: '8px 8px 2px',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0,
    borderRadius: 7,
    border: '1px solid #d9dee1',
    color: '#111b21',
    backgroundColor: '#f8f9fa',
    overflow: 'hidden',
  },
  documentTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px',
  },
  documentLeft: {
    flex: '0 0 auto',
  },
  documentFileTile: {
    width: 46,
    height: 56,
    borderRadius: 4,
    backgroundColor: '#d93025',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentFileIcon: {
    color: '#ffffff',
    fontSize: 16,
    lineHeight: 1,
    marginBottom: 2,
  },
  documentFileType: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: '#ffffff',
  },
  documentMeta: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  documentName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#111b21',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  documentSub: {
    fontSize: 12,
    color: '#667781',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  documentDownload: {
    flex: '0 0 auto',
    width: 32,
    height: 32,
    borderRadius: 16,
    border: '1px solid #e1e5e8',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 2,
  },
  documentDownloadIcon: {
    fontSize: 18,
    color: '#5f6f7a',
    lineHeight: 1,
  },
  missingMedia: {
    padding: 16,
    textAlign: 'center',
    color: '#54656f',
    fontSize: 12,
    borderBottom: '1px solid #d9efce',
    backgroundColor: '#f5f6f6',
  },
  bodyText: {
    padding: '12px 12px 0',
    fontSize: 15,
    lineHeight: '22px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  readMoreButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginLeft: 2,
    display: 'inline',
    verticalAlign: 'baseline',
    color: '#027eb5',
    fontSize: 13.5,
    lineHeight: '20px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  footerText: {
    color: '#667781',
    fontSize: 12.5,
    lineHeight: '16px',
    fontWeight: 500,
    wordBreak: 'break-word',
    flex: '1 1 auto',
    minWidth: 0,
  },
  buttonsWrap: {
    marginTop: 8,
    borderTop: '1px solid #d9efce',
  },
  buttonRow: {
    width: '100%',
    border: 'none',
    borderTop: '1px solid #d9efce',
    backgroundColor: 'transparent',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 14,
    lineHeight: '20px',
    color: '#027eb5',
    fontWeight: 700,
    cursor: 'pointer',
  },
  buttonIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonIcon: {
    fontSize: 16,
    lineHeight: 1,
    color: 'inherit',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    padding: '10px 12px 4px',
  },
  timeText: {
    textAlign: 'right',
    fontSize: 11,
    color: '#667781',
    lineHeight: '14px',
    flex: '0 0 auto',
    marginLeft: 'auto',
  },
  strike: {
    textDecoration: 'line-through',
  },
  code: {
    backgroundColor: '#d8efd0',
    borderRadius: 3,
    padding: '0 4px',
    fontSize: 12.5,
    fontFamily: 'monospace',
  },
  lineWrap: {
    display: 'inline',
  },
  bulletLine: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: 6,
  },
  bulletGlyph: {
    fontSize: '1em',
    lineHeight: '1.2em',
    marginTop: 1,
  },
  bulletText: {
    display: 'inline',
  },
})

export default WhatsappTemplatePreview
